import { useState, type Dispatch, type SetStateAction } from 'react';
import { trpc, trpcClient } from '@/api/trpc';
import { useQueryClient } from '@tanstack/react-query';
import { showToast } from '@/components/Layout/Toast';
import { getBucketIdentityKey, getGroupDisplayLabel, requireScorecardCandidateContextFeatureRef, toSelector } from './helpers';
import type { useScorecardRuleStatus } from '@/hooks/useScorecardRuleStatus';
import type {
    CanonicalParameterFeatureIdentity,
    ParameterDiscoveryMetric,
    ParameterGroup,
    ParameterTableRow,
    ScorecardAuthoringCandidate,
    ScorecardListItem
} from './types';

type ScorecardRuleStatusApi = ReturnType<typeof useScorecardRuleStatus>;

export interface UseScorecardQuickAddOptions {
    customBucketsByIdentityKey: Record<string, ParameterGroup[]>;
    detailCacheScopeKey: string;
    filteredScorecards: ScorecardListItem[];
    invalidateList: () => void;
    isCanonicalValueInScorecard: ScorecardRuleStatusApi['isCanonicalValueInScorecard'];
    lastUsedKey: string | null;
    minEntriesThreshold: number;
    optimizationMode: ParameterDiscoveryMetric;
    parameterDetailsByKey: Record<string, ParameterTableRow>;
    refetchScorecards: () => void;
    selectedScorecardIdTop: number | null;
    setSelectedScorecardIdTop: Dispatch<SetStateAction<number | null>>;
    tableData: ParameterTableRow[];
}

// Quick add of parameter value rules to the selected scorecard (single and bulk)
export const useScorecardQuickAdd = ({
    customBucketsByIdentityKey,
    detailCacheScopeKey,
    filteredScorecards,
    invalidateList,
    isCanonicalValueInScorecard,
    lastUsedKey,
    minEntriesThreshold,
    optimizationMode,
    parameterDetailsByKey,
    refetchScorecards,
    selectedScorecardIdTop,
    setSelectedScorecardIdTop,
    tableData
}: UseScorecardQuickAddOptions) => {
    const queryClient = useQueryClient();

    // Track bulk add state by parameter path to disable that parameter's button while running
    const [bulkAddingPath, setBulkAddingPath] = useState<string | null>(null);

    // Confirmation dialog state for large batches
    const [bulkConfirm, setBulkConfirm] = useState<{
        open: boolean;
        parameterPath: string;
        count: number;
        canonicalFeature?: CanonicalParameterFeatureIdentity;
        scorecardCandidate?: ScorecardAuthoringCandidate;
    } | null>(null);

    // Helper: compute the display groups for a parameter path based on current optimization mode
    const getDisplayGroupsForPath = (
        parameterPath: string
    ): { rowData: ParameterTableRow | undefined; displayGroups: ParameterGroup[] | undefined } => {
        const summaryRow = tableData.find(
            (r: ParameterTableRow) =>
                r.path === parameterPath || r.parameterKey === parameterPath || getBucketIdentityKey(r) === parameterPath
        );

        const rowData =
            parameterDetailsByKey[`${detailCacheScopeKey}:${parameterPath}`] ??
            (summaryRow ? parameterDetailsByKey[`${detailCacheScopeKey}:${getBucketIdentityKey(summaryRow)}`] : undefined) ??
            summaryRow;

        const bucketKey = rowData ? getBucketIdentityKey(rowData) : parameterPath;

        let displayGroups: ParameterGroup[] | undefined;

        if (optimizationMode === 'options') {
            displayGroups = rowData?.optionGroups;
        } else {
            displayGroups = customBucketsByIdentityKey[bucketKey] ?? rowData?.groups;
        }

        return {
            rowData,
            displayGroups: displayGroups?.filter((group) => group.count >= minEntriesThreshold)
        };
    };

    // Helper: compute groups that are not yet in the scorecard for a parameter path
    const getPendingGroupsForPath = (parameterPath: string): ParameterGroup[] => {
        const { rowData, displayGroups } = getDisplayGroupsForPath(parameterPath);
        if (!displayGroups || !rowData?.scorecardCandidate) return [];
        const { scorecardCandidate } = rowData;

        return displayGroups.filter((g) => {
            if (g.hasData === false) return false;
            const label = getGroupDisplayLabel(g);
            const selector = toSelector(label, g.value);

            return !isCanonicalValueInScorecard({
                serializedContextFeatureRef: scorecardCandidate.serializedContextFeatureRef,
                selectorType: selector.type,
                selector
            });
        });
    };

    // Wrapper click handler to optionally show confirmation before bulk adding
    const handleQuickAddAllClick = (parameterRow: ParameterTableRow) => {
        const parameterPath = parameterRow.path;

        if (!selectedScorecardIdTop) {
            showToast('Select a scorecard first (top right)', 'error');
            return;
        }

        // Ensure the selected scorecard is still valid for this scope
        if (!filteredScorecards.some((s) => s.id === selectedScorecardIdTop)) {
            showToast('Selected scorecard is no longer available for this experiment/duration/horizon. Please select another.', 'error');

            setSelectedScorecardIdTop(null);
            if (lastUsedKey) localStorage.removeItem(lastUsedKey);
            return;
        }

        const pending = getPendingGroupsForPath(parameterPath);

        if (pending.length === 0) {
            showToast('No new values to add', 'info');
            return;
        }

        if (pending.length > 20) {
            setBulkConfirm({
                open: true,
                parameterPath,
                count: pending.length,
                canonicalFeature: parameterRow.canonicalFeature,
                scorecardCandidate: parameterRow.scorecardCandidate
            });

            return;
        }

        void quickAddAllForParameter(parameterPath, parameterRow.scorecardCandidate);
    };

    // Quick add to selected scorecard (top)
    const quickAddRule = async (parameterRow: ParameterTableRow, label: string, group: ParameterGroup) => {
        try {
            const { path: parameterPath, scorecardCandidate } = parameterRow;

            if (!selectedScorecardIdTop) {
                showToast('Select a scorecard first (top right)', 'error');
                return;
            }

            // Validate scorecard exists in current filtered list
            if (!filteredScorecards.some((s) => s.id === selectedScorecardIdTop)) {
                showToast(
                    'Selected scorecard is no longer available for this experiment/duration/horizon. Please select another.',
                    'error'
                );

                setSelectedScorecardIdTop(null);
                if (lastUsedKey) localStorage.removeItem(lastUsedKey);
                return;
            }

            const selector = toSelector(label, group.value);

            // Extract lifts and sample size from the groups
            let stockLift = 0;
            let optionLift: number | undefined;
            let sampleSize = group.count || 1;

            // Try to get both stock and option lifts if available
            try {
                const rowData =
                    parameterDetailsByKey[`${detailCacheScopeKey}:${getBucketIdentityKey(parameterRow)}`] ??
                    parameterDetailsByKey[`${detailCacheScopeKey}:${parameterPath}`] ??
                    parameterRow;

                const eqVal = (a: unknown, b: unknown) => {
                    try {
                        return JSON.stringify(a) === JSON.stringify(b);
                    } catch {
                        return a === b;
                    }
                };

                const stockGroup = rowData?.groups?.find(
                    (g: ParameterGroup) => eqVal(g.value, group.value) || (typeof g.valueLabel === 'string' && g.valueLabel === label)
                );

                const optionGroup = rowData?.optionGroups?.find(
                    (g: ParameterGroup) => eqVal(g.value, group.value) || (typeof g.valueLabel === 'string' && g.valueLabel === label)
                );

                if (stockGroup && typeof stockGroup.lift === 'number') {
                    stockLift = stockGroup.lift;
                }

                if (optionGroup && typeof optionGroup.lift === 'number') {
                    optionLift = optionGroup.lift;
                }

                // Use the larger sample size if both are available
                if (stockGroup?.count) sampleSize = Math.max(sampleSize, stockGroup.count);
                if (optionGroup?.count) sampleSize = Math.max(sampleSize, optionGroup.count);
            } catch {
                // best-effort only - use defaults
                stockLift = optimizationMode !== 'options' ? group.lift || 0 : 0;
                optionLift = optimizationMode === 'options' ? group.lift || 0 : undefined;
            }

            const contextFeatureRef = requireScorecardCandidateContextFeatureRef(scorecardCandidate, parameterPath);

            await trpcClient.scorecards.addRule.mutate({
                scorecardId: selectedScorecardIdTop,
                contextFeatureRef,
                selectorType: selector.type,
                selector,
                stockLift,
                optionLift,
                sampleSize
            });

            // Invalidate scorecards query to refresh the list
            invalidateList();
            refetchScorecards();

            // Invalidate rule status query to update UI indicators
            void queryClient.invalidateQueries({
                queryKey: trpc.scorecards.getRulesByScorecard.queryOptions({ scorecardId: selectedScorecardIdTop }).queryKey
            });

            if (lastUsedKey) localStorage.setItem(lastUsedKey, String(selectedScorecardIdTop));
            showToast('Rule added to scorecard', 'success');
        } catch (e) {
            const err = e as Error;
            showToast(err?.message || 'Failed to add rule', 'error');
        }
    };

    // Bulk quick add all values for a parameter to the selected scorecard
    const quickAddAllForParameter = async (parameterPath: string, scorecardCandidate?: ScorecardAuthoringCandidate) => {
        try {
            if (!selectedScorecardIdTop) {
                showToast('Select a scorecard first (top right)', 'error');
                return;
            }

            // Validate scorecard exists in current filtered list
            if (!filteredScorecards.some((s) => s.id === selectedScorecardIdTop)) {
                showToast(
                    'Selected scorecard is no longer available for this experiment/duration/horizon. Please select another.',
                    'error'
                );

                setSelectedScorecardIdTop(null);
                if (lastUsedKey) localStorage.removeItem(lastUsedKey);
                return;
            }

            const { rowData, displayGroups } = getDisplayGroupsForPath(parameterPath);

            if (!rowData) {
                showToast('Parameter not found in current table', 'error');
                return;
            }

            if (!displayGroups || displayGroups.length === 0) {
                showToast('No parameter values available to add', 'warning');
                return;
            }

            const contextFeatureRef = requireScorecardCandidateContextFeatureRef(
                scorecardCandidate ?? rowData.scorecardCandidate,
                parameterPath
            );

            // Compute which groups would be added (dedupe using rule status)
            const pendingGroups = getPendingGroupsForPath(parameterPath);

            if (pendingGroups.length === 0) {
                showToast('All parameter values are already in the scorecard', 'info');
                return;
            }

            // Close any open confirmation dialog for this parameter
            if (bulkConfirm?.open && bulkConfirm.parameterPath === parameterPath) {
                setBulkConfirm(null);
            }

            setBulkAddingPath(parameterPath);

            let added = 0;
            let failed = 0;
            const skipped = (displayGroups?.length || 0) - pendingGroups.length;

            // Perform sequential mutations to avoid overwhelming the backend
            for (const g of pendingGroups) {
                const label = g.valueLabel ?? (typeof g.value === 'boolean' ? (g.value ? 'True' : 'False') : String(g.value));
                const selector = toSelector(label, g.value);

                // Compute lifts and sample size similarly to quickAddRule
                let stockLift = 0;
                let optionLift: number | undefined;
                let sampleSize = g.count || 1;

                try {
                    const stockGroup = rowData.groups?.find((gg) => {
                        try {
                            return JSON.stringify(gg.value) === JSON.stringify(g.value);
                        } catch {
                            return gg.value === g.value || gg.valueLabel === g.valueLabel;
                        }
                    });

                    const optionGroup = rowData.optionGroups?.find((gg) => {
                        try {
                            return JSON.stringify(gg.value) === JSON.stringify(g.value);
                        } catch {
                            return gg.value === g.value || gg.valueLabel === g.valueLabel;
                        }
                    });

                    if (stockGroup && typeof stockGroup.lift === 'number') stockLift = stockGroup.lift;
                    if (optionGroup && typeof optionGroup.lift === 'number') optionLift = optionGroup.lift;
                    if (stockGroup?.count) sampleSize = Math.max(sampleSize, stockGroup.count);
                    if (optionGroup?.count) sampleSize = Math.max(sampleSize, optionGroup.count);
                } catch {
                    stockLift = optimizationMode !== 'options' ? g.lift || 0 : 0;
                    optionLift = optimizationMode === 'options' ? g.lift || 0 : undefined;
                }

                try {
                    await trpcClient.scorecards.addRule.mutate({
                        scorecardId: selectedScorecardIdTop,
                        contextFeatureRef,
                        selectorType: selector.type,
                        selector,
                        stockLift,
                        optionLift,
                        sampleSize
                    });

                    added += 1;
                } catch (err) {
                    console.error('Failed to add rule in bulk:', err);
                    failed += 1;
                }
            }

            // Invalidate after batch completes
            invalidateList();
            refetchScorecards();

            void queryClient.invalidateQueries({
                queryKey: trpc.scorecards.getRulesByScorecard.queryOptions({ scorecardId: selectedScorecardIdTop }).queryKey
            });

            if (lastUsedKey) localStorage.setItem(lastUsedKey, String(selectedScorecardIdTop));

            const parts: string[] = [];
            if (added > 0) parts.push(`added ${added}`);
            if (skipped > 0) parts.push(`skipped ${skipped}`);
            if (failed > 0) parts.push(`failed ${failed}`);
            const summary = parts.join(', ');
            showToast(`Quick Add All complete: ${summary}`, failed > 0 ? 'warning' : 'success');
        } finally {
            setBulkAddingPath(null);
        }
    };

    return {
        bulkAddingPath,
        bulkConfirm,
        setBulkConfirm,
        handleQuickAddAllClick,
        quickAddRule,
        quickAddAllForParameter
    };
};
